<template>
  <div class="modal sign-modal">
    <div class="overlay" @click="handleModalClose"></div>
    <div class="wrapper">
      <img
        class="close"
        @click="handleModalClose"
        src="./../images/close.svg"
      />

      <div class="container">
        <div class="form">
          <div class="form-wrapper">
            <div class="form-fields">
              <div class="form-fields-wrapper">
                <div class="title">{{ modalTitle }}</div>

                <div v-if="isSetupStep" class="qr-setup-container">
                  <div class="desc">
                    Scan this QR code with your authenticator app
                  </div>
                  <div class="qr-code" v-if="qrCode">
                    <img
                      :src="qrCode"
                      alt="QR Code"
                      style="width: 200px; height: 200px"
                    />
                  </div>
                  <div class="loading-indicator" v-else>Loading QR code...</div>
                  <button class="button" @click="moveToVerifyStep">
                    <span>I've scanned the code</span>
                  </button>
                </div>

                <div v-else>
                  <div class="desc">{{ verificationDescription }}</div>

                  <label>
                    <input
                      ref="codeInput"
                      type="text"
                      v-model="verificationCode"
                      placeholder="000000"
                      maxlength="6"
                      @input="onCodeInput"
                      @keyup.enter="handleVerify"
                      class="code-input"
                    />
                  </label>

                  <button
                    class="button"
                    @click="handleVerify"
                    :disabled="loading || verificationCode.length !== 6"
                  >
                    <span v-if="loading">{{ loadingText }}</span>
                    <span v-else>{{ buttonText }}</span>
                  </button>

                  <div v-if="error" class="desc red">{{ error }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "TwoFactorModal",
  props: {
    mode: {
      type: String, // 'enable', 'disable', 'setup', 'verifyLogin'
      required: true,
    },
  },
  data() {
    return {
      verificationCode: "",
    };
  },
  computed: {
    ...mapGetters("twoFactor", [
      "loading",
      "error",
      "qrCode",
      "isSetupMode",
      "setupStep",
    ]),
    isSetupStep() {
      return (
        (this.mode === "enable" || this.mode === "setup" || (this.mode === "verifyLogin" && this.qrCode)) &&
        this.setupStep === "qr"
      );
    },
    isDisabling() {
      return this.mode === "disable";
    },
    modalTitle() {
      if (this.isDisabling) {
        return "Disable Two-Factor Authentication";
      }
      return "Two-Factor Authentication";
    },
    verificationDescription() {
      if (this.mode === "verifyLogin") {
        return "Enter the 6-digit code from your authenticator app to log in.";
      } else if (this.isDisabling) {
        return "Enter your 6-digit code to disable 2FA.";
      } else {
        return "Enter the 6-digit code from your authenticator app.";
      }
    },
    loadingText() {
      if (this.mode === "verifyLogin") {
        return "Verifying...";
      } else if (this.isDisabling) {
        return "Disabling...";
      } else {
        return "Enabling...";
      }
    },
    buttonText() {
      if (this.mode === "verifyLogin") {
        return "Verify";
      } else if (this.isDisabling) {
        return "Disable 2FA";
      } else {
        return "Enable 2FA";
      }
    },
  },
  methods: {
    ...mapActions("twoFactor", ["toggle2FA", "clearError", "resetSetup"]),
    ...mapActions("user", ["fetchProfile"]),

    handleModalClose() {
      this.clearError();
      this.resetSetup();
      this.$emit("close");
    },

    onCodeInput(event) {
      this.verificationCode = event.target.value.replace(/\D/g, "");
    },

    moveToVerifyStep() {
      this.$store.commit("twoFactor/SET_SETUP_STEP", "verify");
    },

    async handleVerify() {
      if (this.verificationCode.length !== 6 || this.loading) {
        return;
      }

      this.$store.commit("twoFactor/SET_ERROR", null);

      if (this.mode === "verifyLogin") {
        this.$emit("verify", this.verificationCode);
      } else {
        try {
          const result = await this.toggle2FA({
            code: this.verificationCode,
            enable: !this.isDisabling,
          });

          if (result.success) {
            const message = this.isDisabling
              ? "Two-factor authentication has been disabled successfully."
              : "Two-factor authentication has been enabled successfully.";

            this.$toast(message, {
              type: "success",
            });
            await this.fetchProfile();
            this.$emit("close");
          }
        } catch (error) {
          const errorMessage =
            error.response?.data?.message || "Invalid verification code";
          this.$store.commit("twoFactor/SET_ERROR", errorMessage);
        }
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (this.$refs.codeInput) {
        this.$refs.codeInput.focus();
      }
    });
  },
  watch: {
    setupStep(newVal) {
      if (newVal === "verify") {
        this.$nextTick(() => {
          if (this.$refs.codeInput) {
            this.$refs.codeInput.focus();
          }
        });
      }
    },
  },
};
</script>

<style scoped>
.profile-page .form .title {
  width: fit-content;
}
.profile-page .wrapper {
  padding: 8px;
}

.code-input {
  text-align: center;
  font-size: 18px;
  letter-spacing: 4px;
  font-weight: bold;
}

.code-input::placeholder {
  text-align: center;
}

.desc {
  margin: 10px 0;
  text-align: center;
}

.desc.link {
  cursor: pointer;
  color: #007bff;
  text-decoration: underline;
}

.desc.link:hover {
  color: #0056b3;
}

.desc.red {
  color: #dc3545;
}

.button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.qr-setup-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 20px 0;
}

.qr-code {
  margin: 20px 0;
  padding: 10px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.loading-indicator {
  margin: 20px 0;
  color: #666;
}

.setup-step {
  text-align: center;
  margin: 20px 0;
}

.setup-step h3 {
  margin-top: 0;
  margin-bottom: 15px;
}

.setup-step p {
  margin-bottom: 20px;
}
</style>
